import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, RotateCw } from 'lucide-react';
import api from '../api';
import Swal from 'sweetalert2';

export default function BulkAssignTaskModal({ isOpen, onClose, employees = [] }) {
  const [loading, setLoading] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: '',
    start_date: new Date().toISOString().split('T')[0],
    due_date: '',
    estimated_hours: ''
  });

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedEmployees([]);
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        category: '',
        start_date: new Date().toISOString().split('T')[0],
        due_date: '',
        estimated_hours: ''
      });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const toggleEmployee = (id) => {
    if (selectedEmployees.includes(id)) {
      setSelectedEmployees(selectedEmployees.filter(empId => empId !== id));
    } else {
      setSelectedEmployees([...selectedEmployees, id]);
    }
  };

  const handleSelectAll = () => {
    setSelectedEmployees(employees.map(emp => emp.id));
  };

  const handleClear = () => {
    setSelectedEmployees([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedEmployees.length === 0) {
      Swal.fire('Error', 'Please select at least one employee', 'warning');
      return;
    }
    if (!formData.title || !formData.due_date) {
      Swal.fire('Error', 'Title and Due Date are required', 'warning');
      return;
    }

    try {
      setLoading(true);
      await api.post('/admin/tasks/bulk-assign', {
        ...formData,
        employee_ids: selectedEmployees
      });
      Swal.fire('Success', `Task assigned to ${selectedEmployees.length} employees`, 'success');
      onClose();
    } catch (err) {
      console.error("Error in bulk assign:", err);
      Swal.fire('Error', 'Failed to assign tasks', 'error');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-hidden">
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-md transition-opacity" 
        onClick={onClose}
      ></div>
      
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[550px] flex flex-col max-h-[95vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header - Orange */}
        <div className="px-6 py-4 bg-[#f97316] flex items-center justify-between shadow-lg z-20">
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-white"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            <h2 className="text-lg font-black text-white tracking-wide uppercase">Bulk Assign Task</h2>
          </div>
          <button 
            onClick={onClose}
            className="p-1 text-white/70 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form Body */}
        <div className="p-6 overflow-y-auto custom-scrollbar flex-1 bg-slate-50/30">
          <form id="bulk-assign-form" onSubmit={handleSubmit} className="space-y-4">
            
            {/* Select Employees Box */}
            <div className="space-y-2">
              <div className="flex justify-between items-end">
                <label className="text-[11px] font-black text-slate-700 uppercase tracking-widest">Select Employees <span className="text-red-500">*</span></label>
                <div className="text-[10px] font-black">
                  <span 
                    className="text-purple-600 cursor-pointer hover:text-purple-700 uppercase tracking-wider mx-2 underline"
                    onClick={handleSelectAll}
                  >
                    Select All
                  </span>
                  <span 
                    className="text-slate-400 cursor-pointer hover:text-slate-600 uppercase tracking-wider underline"
                    onClick={handleClear}
                  >
                    Clear
                  </span>
                </div>
              </div>
              
              <div className="bg-white border border-slate-200 rounded-xl p-4 grid grid-cols-2 gap-3 max-h-[160px] overflow-y-auto custom-scrollbar shadow-inner">
                {employees.map((emp) => (
                  <label key={emp.id} className="flex items-center gap-2.5 cursor-pointer group p-1 hover:bg-slate-50 rounded-lg transition-colors">
                    <input 
                      type="checkbox" 
                      className="w-4 h-4 rounded-md border-slate-300 text-orange-500 focus:ring-orange-500 shadow-sm transition-all" 
                      checked={selectedEmployees.includes(emp.id)}
                      onChange={() => toggleEmployee(emp.id)}
                    />
                    <span className="text-[12px] font-bold text-slate-600 group-hover:text-slate-900 transition-colors truncate">{emp.name}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] font-black text-orange-500 uppercase tracking-widest bg-orange-50 px-3 py-1.5 rounded-lg inline-block">
                {selectedEmployees.length} employees selected
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-black text-slate-700 uppercase tracking-widest">Task Title <span className="text-red-500">*</span></label>
              <input 
                type="text" 
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Enter task title" 
                className="w-full border border-slate-200 rounded-lg text-[13px] font-bold text-slate-700 py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-colors shadow-sm"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-black text-slate-700 uppercase tracking-widest">Description</label>
              <textarea 
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Enter task description..." 
                className="w-full border border-slate-200 rounded-lg text-[13px] font-semibold text-slate-700 py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-colors shadow-sm resize-none"
              ></textarea>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-slate-700 uppercase tracking-widest">Priority</label>
                <select 
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className="w-full border border-slate-200 rounded-lg text-[13px] font-bold text-slate-700 py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-colors shadow-sm appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M6%209L12%2015L18%209%22%20stroke%3D%22%2364748B%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[center_right_10px]"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-slate-700 uppercase tracking-widest">Category</label>
                <input 
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  placeholder="e.g. Admission" 
                  className="w-full border border-slate-200 rounded-lg text-[13px] font-bold text-slate-700 py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-colors shadow-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-slate-700 uppercase tracking-widest">Start Date</label>
                <input 
                  type="date" 
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  className="w-full border border-slate-200 rounded-lg text-[12px] font-bold text-slate-700 py-2.5 px-3 focus:outline-none shadow-sm"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-slate-700 uppercase tracking-widest">Due Date <span className="text-red-500">*</span></label>
                <input 
                  type="date" 
                  value={formData.due_date}
                  onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                  className="w-full border border-slate-200 rounded-lg text-[12px] font-bold text-slate-700 py-2.5 px-3 focus:outline-none shadow-sm"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-black text-slate-700 uppercase tracking-widest">Est. Hours</label>
              <input 
                type="number" 
                value={formData.estimated_hours}
                onChange={(e) => setFormData({...formData, estimated_hours: e.target.value})}
                placeholder="e.g. 4" 
                className="w-full border border-slate-200 rounded-lg text-[13px] font-bold text-slate-700 py-2.5 px-3 focus:outline-none shadow-sm"
              />
            </div>

          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-white border-t border-slate-100 flex items-center justify-between gap-3 z-10 shadow-[0_-10px_30px_rgba(0,0,0,0.03)]">
          <button 
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-3 font-black text-[11px] text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors shadow-sm uppercase tracking-widest"
          >
            Cancel
          </button>
          <button 
            type="submit"
            form="bulk-assign-form"
            disabled={loading || selectedEmployees.length === 0}
            className={`flex-1 px-4 py-3 font-black text-[11px] flex justify-center items-center gap-2 text-white bg-[#f97316] border border-[#ea580c] rounded-xl hover:bg-[#ea580c] shadow-lg shadow-orange-500/20 transition-all uppercase tracking-widest active:scale-95 disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed`}
          >
            {loading ? <RotateCw className="animate-spin" size={16} /> : <Send size={16} />} 
            Assign to {selectedEmployees.length} Employees
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
}
